<script setup lang="ts">
import { AlertTriangle, Inbox, LoaderCircle, RotateCw } from '@lucide/vue'

const props = withDefaults(defineProps<{
  loading?: boolean
  error?: string
  empty?: boolean
  title?: string
  message?: string
  loadingText?: string
  emptyTitle?: string
  errorTitle?: string
}>(), {
  loading: false,
  error: '',
  empty: false,
  title: '',
  message: '',
  loadingText: '正在加载数据',
  emptyTitle: '暂无数据',
  errorTitle: '数据加载失败'
})

const emit = defineEmits<{ retry: [] }>()
</script>

<template>
  <section v-if="props.loading" class="rr-resource-state" role="status" aria-live="polite" aria-busy="true">
    <LoaderCircle aria-hidden="true" />
    <strong>{{ props.title || props.loadingText }}</strong>
  </section>
  <section v-else-if="props.error" class="rr-resource-state is-error" role="alert">
    <AlertTriangle aria-hidden="true" />
    <div>
      <strong>{{ props.title || props.errorTitle }}</strong>
      <p>{{ props.message || props.error }}</p>
    </div>
    <button type="button" class="rr-resource-state__retry" @click="emit('retry')">
      <RotateCw aria-hidden="true" />
      <slot name="action">重试</slot>
    </button>
  </section>
  <section v-else-if="props.empty" class="rr-resource-state" aria-live="polite">
    <Inbox aria-hidden="true" />
    <strong>{{ props.emptyTitle }}</strong>
  </section>
  <slot v-else />
</template>

<style scoped>
.rr-resource-state {
  display: grid;
  min-width: 0;
  place-items: center;
  align-content: center;
  gap: var(--rr-space-2);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-panel);
  background: var(--rr-color-surface);
  color: var(--rr-color-text-muted);
  padding: var(--rr-space-6);
  text-align: center;
}

.rr-resource-state.is-error {
  grid-template-columns: auto minmax(0, 1fr) auto;
  place-items: center start;
  border-color: var(--rr-color-danger-border);
  background: var(--rr-color-danger-soft);
  color: var(--rr-color-danger);
  text-align: left;
}

.rr-resource-state strong {
  color: var(--rr-color-text);
}

.rr-resource-state.is-error strong,
.rr-resource-state.is-error p {
  color: inherit;
}

.rr-resource-state p {
  margin: 0;
  overflow-wrap: anywhere;
}

.rr-resource-state__retry {
  display: inline-flex;
  align-items: center;
  gap: var(--rr-space-1);
  border: 1px solid var(--rr-color-danger-border);
  border-radius: var(--rr-radius-control);
  background: var(--rr-color-surface);
  color: var(--rr-color-danger);
  padding: var(--rr-space-1) var(--rr-space-2);
}
</style>
